import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import { Skeleton } from './feedback';

export interface StatProps {
  label: string;
  /** Already formatted (formatINR, formatPercent...). Pass undefined while loading. */
  value: ReactNode;
  icon?: LucideIcon;
  /** One line of context under the number: "46 of 50 beds". */
  detail?: ReactNode;
  /** Alias for detail */
  subtext?: ReactNode;
  /** Trend vs the previous period. `good` says whether up is good news. */
  delta?: { value: string; direction: 'up' | 'down'; good?: boolean };
  /** Makes the tile a link to the underlying list. */
  to?: string;
  tone?: 'default' | 'danger';
  loading?: boolean;
  className?: string;
}

/** KPI tile. Numbers use tabular figures so columns of tiles line up. */
export function Stat({ label, value, icon: Icon, detail, subtext, delta, to, tone = 'default', loading, className }: StatProps) {
  const subtitle = detail ?? subtext;
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        {Icon && <Icon className="size-4 text-slate-400" aria-hidden />}
      </div>
      <div className="mt-2">
        {loading ? (
          <Skeleton className="h-8 w-28" />
        ) : (
          <p className={cn('tabular font-display text-2xl font-bold leading-none', tone === 'danger' ? 'text-red-700' : 'text-slate-900')}>{value}</p>
        )}
      </div>
      <div className="mt-2 flex min-h-5 flex-wrap items-center gap-x-2 text-xs text-slate-500">
        {loading ? (
          <Skeleton className="h-3.5 w-36" />
        ) : (
          <>
            {delta && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 font-semibold',
                  (delta.direction === 'up') === (delta.good ?? true) ? 'text-emerald-700' : 'text-red-700',
                )}
              >
                {delta.direction === 'up' ? <ArrowUpRight className="size-3.5" aria-hidden /> : <ArrowDownRight className="size-3.5" aria-hidden />}
                {delta.value}
              </span>
            )}
            {subtitle && <span>{subtitle}</span>}
          </>
        )}
      </div>
    </>
  );

  const classes = cn('block rounded-card border border-slate-200 bg-white p-4 shadow-card', to && 'transition-shadow hover:shadow-raised', className);
  return to ? (
    <Link to={to} className={classes}>
      {body}
    </Link>
  ) : (
    <div className={classes}>{body}</div>
  );
}

export function StatGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-2 lg:grid-cols-4 gap-4', className)}>{children}</div>;
}
